import { findWrapping, liftTarget, ReplaceAroundStep, canSplit } from 'prosemirror-transform';
import { NodeExtension } from '../../../core/index.js';
import { TextSelection, PluginKey } from 'prosemirror-state';
import { NodeRange, Fragment, Slice } from 'prosemirror-model';
import { v4 } from 'uuid';
import 'prosemirror-commands';
import { InputRule } from 'prosemirror-inputrules';
import { gapCursorSituation, GapCursor } from '../../../citadel/kit/GapcursorSelection/index.js';

var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
var findParentNode = function(predicate) {
    return function(param) {
        var $from = param.$from;
        return findParentNodeClosestToPos($from, predicate);
    };
};
// 类型是否可以插入富文本
var canInsertTypes = function(state) {
    var parentNode = state.selection.$from.parent;
    var textType = [
        "status",
        "code_block",
        "note_title",
        "collapse_title"
    ];
    return !textType.includes(parentNode.type.name);
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var listTypes = [
    "bullet_list",
    "ordered_list",
    "task_list"
];
var itemTypes = [
    "list_item",
    "task_item"
];
function doWrapInList(tr, range, wrappers, joinBefore, listType, isSelectRange) {
    var content = Fragment.empty;
    for(var i = wrappers.length - 1; i >= 0; i--){
        // 新增列表时如果遇到需要被序列为列表的内容有缩进的情况 需要把缩进属性添加到列表上并去除列表序列内容的缩进
        var attrs = null;
        if (!isSelectRange && wrappers[i].type === listType) {
            attrs = {
                indent: range.$from.parent.attrs.indent
            };
        }
        content = Fragment.from(wrappers[i].type.create(attrs || wrappers[i].attrs, content));
    }
    tr.step(new ReplaceAroundStep(range.start - (joinBefore ? 2 : 0), range.end, range.start, range.end, new Slice(content, 0, 0), wrappers.length, true));
    var found = 0;
    for(var i1 = 0; i1 < wrappers.length; i1++){
        if (wrappers[i1].type === listType) found = i1 + 1;
    }
    var splitDepth = wrappers.length - found;
    var splitPos = range.start + wrappers.length - (joinBefore ? 2 : 0);
    var parent = range.parent;
    var childNodes = [];
    for(var i2 = range.startIndex, e = range.endIndex, first = true; i2 < e; i2++, first = false){
        var childNode = parent.child(i2);
        childNodes.push(childNode);
        if (!first && canSplit(tr.doc, splitPos, splitDepth)) {
            tr.split(splitPos, splitDepth);
            splitPos += 2 * splitDepth;
        }
        splitPos += parent.child(i2).nodeSize;
    }
    // 去除列表序列内容的缩进
    tr.doc.nodesBetween(range.start, /**
     * range 的 start和end信息只能包含序列内容的范围 列表的结构如下 子节点length*2代表li标签的范围， +2代表外层ul/ol标签的范围
     * 三者相加之后才可以完整包含整个列表的范围
     * <ol>
     *    <li>序列内容</li>
     *    ...
     * </ol>
     */ range.end + childNodes.length * 2 + 2, function(node, pos) {
        if (childNodes.includes(node) && node.attrs.indent !== 0) {
            tr.setNodeMarkup(pos, null, _objectSpreadProps(_objectSpread({}, node.attrs), {
                indent: 0
            }));
        }
    });
    return tr;
}
function liftToOuterList(tr, itemType, range) {
    var transaction = tr;
    var end = range.end;
    var endOfList = range.$to.end(range.depth);
    if (end < endOfList) {
        // There are siblings after the lifted items, which must become
        // children of the last item
        transaction.step(new ReplaceAroundStep(end - 1, endOfList, end, endOfList, new Slice(Fragment.from(itemType.create(null, range.parent.copy())), 1, 0), 1, true));
        range = new NodeRange(transaction.doc.resolve(range.$from.pos), transaction.doc.resolve(endOfList), range.depth);
    }
    return transaction.lift(range, liftTarget(range));
}
function liftOutOfList(tr, range) {
    var transaction = tr;
    var list = range.parent;
    // Merge the list items into a single big item
    for(var pos = range.end, i = range.endIndex - 1, e = range.startIndex; i > e; i--){
        pos -= list.child(i).nodeSize;
        transaction.delete(pos - 1, pos + 1);
    }
    var $start = transaction.doc.resolve(range.start);
    var item = $start.nodeAfter;
    var atStart = range.startIndex === 0;
    var atEnd = range.endIndex === list.childCount;
    var parent = $start.node(-1);
    var indexBefore = $start.index(-1);
    if (!parent.canReplace(indexBefore + (atStart ? 0 : 1), indexBefore + 1, item.content.append(atEnd ? Fragment.empty : Fragment.from(list)))) {
        return false;
    }
    var start = $start.pos;
    var end = start + item.nodeSize;
    // 单独处理 data-diff-id 问题，后半段的 list 认为是新的 list，data-diff-id 重新生成
    // 详见 https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/60669246
    var newList = list.copy(Fragment.empty);
    newList.attrs = _objectSpread({}, newList.attrs);
    newList.attrs.dataDiffId = "ct-diff-id-".concat(v4());
    // Strip off the surrounding list. At the sides where we're not at
    // the end of the list, the existing list is closed. At sides where
    // this is the end, it is overwritten to its end.
    transaction.step(new ReplaceAroundStep(start - (atStart ? 1 : 0), end + (atEnd ? 1 : 0), start + 1, end - 1, new Slice((atStart ? Fragment.empty : Fragment.from(list.copy(Fragment.empty))).append(atEnd ? Fragment.empty : Fragment.from(newList)), atStart ? 0 : 1, atEnd ? 0 : 1), atStart ? 0 : 1));
    return transaction;
}
// :: (NodeType, ?Object) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Returns a command function that wraps the selection in a list with
// the given type an attributes. If `dispatch` is null, only return a
// value to indicate whether this is possible, but don't actually
// perform the change.
function wrapInList(listType, attrs) {
    return function(tr, selRange) {
        var $to = selRange.$to;
        var $from = selRange.$from;
        // 根据 schema ，list_item 里面必须是 block 节点 如果 $from.parent 不是 block 先矫正 $from
        if (!$from.parent.isBlock) {
            var parentBlockNode = findParentNode(function(node) {
                return node.type.isBlock;
            })(selRange);
            $from = tr.doc.resolve(parentBlockNode.pos);
        }
        var transaction = tr;
        var range = $from.blockRange($to);
        // 光标（gaocursor）在块级元素前后时，需要设置 range 为该块元素，否则会无法设置列表
        if (_instanceof(selRange, GapCursor)) {
            if (selRange.gapCursorType === gapCursorSituation.afterNode && $from.nodeBefore) {
                range = new NodeRange(tr.doc.resolve($from.pos - $from.nodeBefore.nodeSize), $from, $from.depth);
            }
            if (selRange.gapCursorType === gapCursorSituation.beforeNode && $from.nodeAfter) {
                range = new NodeRange($from, tr.doc.resolve($from.pos + $from.nodeAfter.nodeSize), $from.depth);
            }
        }
        var doJoin = false;
        var outerRange = range;
        if (!range) return false;
        // This is at the top of an existing list item
        if (range.depth >= 2 && $from.node(range.depth - 1).type.compatibleContent(listType) && range.startIndex === 0) {
            // Don't do anything if this is the top of the list
            if ($from.index(range.depth - 1) === 0) return false;
            var $insert = transaction.doc.resolve(range.start - 2);
            outerRange = new NodeRange($insert, $insert, range.depth);
            if (range.endIndex < range.parent.childCount) {
                range = new NodeRange($from, transaction.doc.resolve($to.end(range.depth)), range.depth);
            }
            doJoin = true;
        }
        var wrap = findWrapping(outerRange, listType, attrs, range);
        if (!wrap) return false;
        var fromParent = $from.parent;
        var toParent = $to.parent;
        var isSelectRange = fromParent !== toParent || fromParent.type.name === "doc" && toParent.type.name === "doc";
        return doWrapInList(transaction, range, wrap, doJoin, listType, isSelectRange);
    };
}
// :: (NodeType) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Create a command to lift the list item around the selection up into
// a wrapping list.
function liftListItem(itemType) {
    return function(tr, selRange) {
        var transaction = tr;
        var $from = selRange.$from, $to = selRange.$to;
        var range = $from.blockRange($to, function(node) {
            return node.childCount && node.firstChild.type === itemType;
        });
        if (!range) return false;
        if ($from.node(range.depth - 1).type === itemType) {
            // Inside a parent list
            return liftToOuterList(transaction, itemType, range);
        }
        // Outer list node
        return liftOutOfList(transaction, range);
    };
}
function findListParentNode(selection) {
    // 找离当前光标最近的容器节点（不算 list_item 类型）
    var conatinerNode = findParentNode(function(n) {
        return n.isBlock && !n.isTextblock && !itemTypes.includes(n.type.name);
    })(selection);
    // 如果最近的容器节点是 list 类型，则返回该节点，否则返回 Null
    return conatinerNode && listTypes.includes(conatinerNode.node.type.name) ? conatinerNode : null;
}

// import { liftListItem } from "prosemirror-schema-list";
var handleListClick = function(listType) {
    return function(state, dispatch) {
        var tr = state.tr, selection = state.selection, schema = state.schema;
        var _schema_nodes = schema.nodes, list_item = _schema_nodes.list_item, task_item = _schema_nodes.task_item, bullet_list = _schema_nodes.bullet_list, ordered_list = _schema_nodes.ordered_list, task_list = _schema_nodes.task_list;
        var itemTypes = [
            list_item,
            task_item
        ];
        var listTypes = [
            bullet_list,
            ordered_list,
            task_list
        ];
        var transaction = tr;
        if (selection.forEachCell) {
            selection.ranges.forEach(function(originRange, i) {
                // 当前面的range增加列表后，range的位置发生改变，需要用 transaction.selection
                var _transaction_selection_ranges_i = transaction.selection.ranges[i], $from = _transaction_selection_ranges_i.$from, $to = _transaction_selection_ranges_i.$to;
                var range = $from.blockRange($to);
                if (!range) return false;
                var listNode = $from.parent.firstChild;
                // 当前不在任何列表中，新增列表
                if (!listNode || listNode.type !== listType) {
                    var newTr = wrapInList(listType)(transaction, range);
                    if (!newTr) return false;
                    transaction = newTr;
                } else {
                    var trueSelection = TextSelection.create(transaction.doc, $from.pos + 2, $to.pos - 2);
                    var itemType = listType === task_list ? task_item : list_item;
                    var trueRange = trueSelection.$from.blockRange(trueSelection.$to, function(node) {
                        return node.childCount && node.firstChild.type === itemType;
                    });
                    if (!trueRange) return false;
                    // 选中的列表类型与当前的列表类型一致，提升列表
                    var newTr1 = liftOutOfList(transaction, trueRange);
                    if (!newTr1) return false;
                    transaction = newTr1;
                }
            });
            if (!transaction) return false;
            if (dispatch) dispatch(transaction);
            return true;
        }
        // 如果最近的容器节点是 list 类型，则是改变当前列表类型，否则是新增列表
        var listNodeInSelFrom = findListParentNode(selection);
        // 当前selection.from不在任何列表中，新增列表
        if (!listNodeInSelFrom) {
            transaction = wrapInList(listType)(transaction, selection);
            if (!transaction) return false;
            if (dispatch) dispatch(transaction);
            return true;
        }
        var listNodes = [];
        // 遍历选区，找到所有列表节点
        transaction.doc.nodesBetween(selection.from, selection.to, function(node, pos) {
            // 当前节点在选区内（list至少长度为4）
            if (listTypes.includes(node.type) && pos + 4 > listNodeInSelFrom.pos) {
                listNodes.push({
                    node: node,
                    pos: pos
                });
            }
        });
        listNodes.forEach(function(listNode) {
            var listNodePos = transaction.mapping.map(listNode.pos);
            // 提升列表时，只对当前选区内的列表节点进行操作
            var from = Math.max(listNodePos + 1, transaction.selection.from);
            var to = Math.min(listNode.node.nodeSize + listNodePos - 1, transaction.selection.to);
            // 选中的列表类型与当前的列表类型一致，提升列表
            var itemType = listType === task_list ? task_item : list_item;
            if (listNode.node.type === listType) {
                var newTr = liftListItem(itemType)(transaction, {
                    $from: transaction.doc.resolve(from),
                    $to: transaction.doc.resolve(to)
                });
                if (newTr) {
                    transaction = newTr;
                    return;
                }
            }
            // 转换列表类型
            from = listNodePos;
            to = listNode.node.nodeSize + from;
            // 转换当前列表类型为选中的类型
            transaction.doc.nodesBetween(from, to, function(node, pos) {
                if (listTypes.includes(node.type) && node.type !== listType) {
                    // 当前节点在选区内（list至少长度为4）
                    if (pos + 4 > from) {
                        transaction = transaction.setNodeMarkup(pos, listType, node.attrs);
                    }
                } else if (itemTypes.includes(node.type) && node.type !== itemType) {
                    // 当前节点在选区内（listItem至少长度为2）
                    if (pos + 2 > from) {
                        transaction = transaction.setNodeMarkup(pos, itemType, node.attrs);
                    }
                }
            });
        });
        dispatch(transaction);
        return true;
    };
};

var isListActive = function(type) {
    return function() {
        return function(state) {
            var listNode = findListParentNode(state.selection);
            return listNode && listNode.node.type === type;
        };
    };
};

var ExtensionType;
(function(ExtensionType) {
    ExtensionType["Node"] = "node";
    ExtensionType["Mark"] = "mark";
    ExtensionType["NodeEmbed"] = "node-embed";
})(ExtensionType || (ExtensionType = {}));

/**
 * 通用配置
 */ var pluginKey = new PluginKey("image");

var isCommonDisabled = function() {
    return function(state) {
        return !canInsertTypes(state);
    };
};

var convertCommand = function(commandFunction) {
    return function(param) {
        var state = param.state, dispatch = param.dispatch, view = param.view, args = param.args;
        return commandFunction(state, dispatch, view, args);
    };
};

/**
 * keys--不区分大小写
 */ // "META_KEY-0"-正文
// 有序列表
var regexpOrderedList = /^1\.\s$/;

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = ".pm-body-wrapper ol,.ProseMirror ol{margin:8px 0;padding-left:24px}.pm-body-wrapper ol li[data-list-item-diff-id],.ProseMirror ol li[data-list-item-diff-id]{min-height:24px;font-size:14px;list-style:inherit}.pm-body-wrapper ol>h1,.pm-body-wrapper ol>h2,.pm-body-wrapper ol>h3,.pm-body-wrapper ol>h4,.pm-body-wrapper ol>h5,.pm-body-wrapper ol>h6,.pm-body-wrapper ol>p,.ProseMirror ol>h1,.ProseMirror ol>h2,.ProseMirror ol>h3,.ProseMirror ol>h4,.ProseMirror ol>h5,.ProseMirror ol>h6,.ProseMirror ol>p{margin:0}.pm-body-wrapper ol .ct-image,.ProseMirror ol .ct-image{vertical-align:top}.pm-body-wrapper ol hr,.ProseMirror ol hr{display:inline-block}.pm-body-wrapper .ct-item-level-1,.ProseMirror .ct-item-level-1{font-size:28px!important}.pm-body-wrapper .ct-item-level-2,.ProseMirror .ct-item-level-2{font-size:24px!important}.pm-body-wrapper .ct-item-level-3,.ProseMirror .ct-item-level-3{font-size:20px!important}.pm-body-wrapper .ct-item-level-4,.ProseMirror .ct-item-level-4{font-size:16px!important}.pm-body-wrapper .ct-item-level-5,.ProseMirror .ct-item-level-5{font-size:14px!important}.pm-body-wrapper .ct-item-level-6,.ProseMirror .ct-item-level-6{font-size:12px!important}ol .ct-item-level-1{margin-left:12px}ol .ct-item-level-2{margin-left:8px}ol .ct-item-level-3{margin-left:5px}ol .ct-item-level-4{margin-left:1px}ol .ct-item-level-6{margin-left:-2px}ol{list-style-type:decimal}ol ol{list-style-type:lower-alpha}ol ol ol{list-style-type:lower-roman}ol ol ol ol{list-style-type:decimal}ol ol ol ol ol{list-style-type:lower-alpha}ol ol ol ol ol ol{list-style-type:lower-roman}ol ol ol ol ol ol ol{list-style-type:decimal}ol ol ol ol ol ol ol ol{list-style-type:lower-alpha}ol ol ol ol ol ol ol ol ol{list-style-type:lower-roman}";
styleInject(css_248z);

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var OrderedListExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits(OrderedListExtension, NodeExtension);
    var _super = _createSuper(OrderedListExtension);
    function OrderedListExtension() {
        _classCallCheck(this, OrderedListExtension);
        return _super.apply(this, arguments);
    }
    var _proto = OrderedListExtension.prototype;
    _proto.commands = function commands(param) {
        var type = param.type;
        return {
            handleListClick: function() {
                return handleListClick(type);
            },
            isOrderedListActive: isListActive(type),
            isOrderedListDisabled: isCommonDisabled
        };
    };
    _proto.keys = function keys(param) {
        var type = param.type;
        return {
            "Mod-Shift-o": convertCommand(handleListClick(type))
        };
    };
    _proto.inputRules = function inputRules(param) {
        var type = param.type, getView = param.getView;
        return [
            new InputRule(new RegExp(regexpOrderedList), function(state, match, start, end) {
                var range = state.tr.doc.resolve(start).blockRange();
                var wrapping = range && findWrapping(range, type);
                if (!wrapping) return null;
                var view = getView();
                var transaction = view.state.tr.deleteRange(start, end);
                transaction = wrapInList(type)(transaction, transaction.selection);
                if (transaction) {
                    view.dispatch(transaction);
                    return view.state.tr; // 返回空tr表示已经处理inputRule
                }
                return null;
            })
        ];
    };
    _createClass(OrderedListExtension, [
        {
            key: "name",
            get: function get() {
                return "ordered_list";
            }
        }
    ]);
    return OrderedListExtension;
}(NodeExtension);

export { OrderedListExtension };
//# sourceMappingURL=index.js.map
